import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, Typography } from '@mui/material';
import moment from 'moment';
import { addDays, format, startOfWeek, subDays } from 'date-fns';

import { FBoxContainer, FHAutocomplete } from '../../base';
import { FORTA_BLUE, FORTA_GRAY4, FORTA_WHITE } from '../../../utils/colors';
import { areUserRolesIn } from '../../../helpers';
import {
  clientActions,
  clientSupervisionActions,
  parentSupervisionActions,
  providerAssignmentActions,
  utilizationActions,
} from '../../../store';
import { Roles } from '../../../constants';
import SixWeekDocumentUtilizationSummary from './utilization/SixWeekDocumentUtilizationSummary';
import ClientInsightDetails from './ClientInsightDetails';
import { getUtcOffset } from '../../../utils/common';
import { mergeClientParentSessionUtilization, processClientParentSNUtilizations } from './utils';

import type { RootState } from '../../../store';
import type { IClient, IOption } from '../../../utils/types';

const boxContainerStyle = {
  borderRadius: '20px',
  mt: 3,
  flexGrow: 0,
  boxShadow: 'none',
};

const boxContainerHeaderStyle = {
  height: 40,
  flexGrow: 0,
  boxShadow: 'none',
  borderRadius: '16px 16px 0 0',

  '& h4': {
    fontSize: 20,
    fontWeight: 500,
  },
};

export interface WeeklyUtilization {
  name: string;
  parentSN: number;
  clientSN: number;
  session: number;
  clientSNExpectedHours: number;
}

export type IClientParentSNUtilization = { [key: string]: { parentSN: number; clientSN: number } };

function ClientInsights() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { list: clientList = [], loading: clientLoading } = useSelector(
    (state: RootState) => state.clients,
  );

  const { current: treatmentPlanInfo, currentTpLoading: tpInfoLoading } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );
  const { user } = useSelector((state: any) => state.auth);

  const { data: sessionUtilization = [], loading: sessionUtilizationLoading } = useSelector(
    (state: RootState) => state.utilizations,
  );

  const [selectedClient, setSelectedClient] = useState<IOption | null>(null);
  const [clientParentSNUtilization, setClientParentSNUtilization] =
    useState<IClientParentSNUtilization | null>(null);
  const [sixWeeksUtilizationData, setSixWeeksUtilizationData] = useState<WeeklyUtilization[]>([]);
  const [clientParentUtilizationLoading, setClientParentUtilizationLoading] =
    useState<boolean>(false);

  const showDocumentUtilizationSummary = areUserRolesIn(user?.roles, [Roles.Bcba]);

  useEffect(() => {
    if (!clientParentSNUtilization || !sessionUtilization) return;
    const result = mergeClientParentSessionUtilization(
      clientParentSNUtilization,
      sessionUtilization,
    );
    setSixWeeksUtilizationData(result.reverse());
  }, [clientParentSNUtilization, sessionUtilization]);

  useEffect(() => {
    if (selectedClient) {
      const clientId = Number(selectedClient.id);
      if (Number.isNaN(clientId)) return;
      dispatch(clientActions.getClient({ clientId, getAccessTokenSilently }));
      dispatch(
        providerAssignmentActions.getList({
          getAccessTokenSilently,
          active_only: true,
          client_id: clientId,
        }),
      );

      if (showDocumentUtilizationSummary) {
        const startDate = format(
          subDays(startOfWeek(new Date(), { weekStartsOn: 6 }), 7 * 5),
          'yyyy-MM-dd',
        );
        const endDate = format(
          addDays(startOfWeek(new Date(), { weekStartsOn: 6 }), 6),
          'yyyy-MM-dd',
        );
        const utcOffset = getUtcOffset(moment());

        dispatch(
          utilizationActions.getUtilization({
            getAccessTokenSilently,
            clientIds: clientId,
            startDate,
            endDate,
            utcOffset,
          }),
        );
        fetchSixWeeksClientParentUtilization(
          clientId,
          format(startOfWeek(new Date(), { weekStartsOn: 6 }), 'yyyy-MM-dd'),
          endDate,
        );
      }
    }

    return () => {
      dispatch(clientActions.clearSelectedClient());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, showDocumentUtilizationSummary]);

  const fetchSixWeeksClientParentUtilization = async (
    clientId: number,
    startOfWeek: string,
    endOfWeek: string,
  ) => {
    const dateArray: string[][] = [[startOfWeek, endOfWeek]];
    for (let i = 1; i < 6; i++) {
      const start = moment(startOfWeek)
        .subtract(7 * i, 'days')
        .format('YYYY-MM-DD');
      const end = moment(endOfWeek)
        .subtract(7 * i, 'days')
        .format('YYYY-MM-DD');
      dateArray.push([start, end]);
    }
    const parentSNPromises = dateArray.map((dates) => {
      return dispatch(
        parentSupervisionActions.getAggregations({
          getAccessTokenSilently,
          clientId,
          start: dates[0],
          end: dates[1],
        }),
      );
    });

    const clientSNPromises = dateArray.map((dates) => {
      return dispatch(
        clientSupervisionActions.getAggregations({
          getAccessTokenSilently,
          clientId,
          start: dates[0],
          end: dates[1],
        }),
      );
    });

    setClientParentUtilizationLoading(true);
    const parentUtilization = await Promise.all(parentSNPromises);
    const clientUtilization = await Promise.all(clientSNPromises);
    setClientParentUtilizationLoading(false);

    const results = processClientParentSNUtilizations(
      clientUtilization.map((it) => it.payload),
      parentUtilization.map((it) => it.payload),
      dateArray,
    );
    setClientParentSNUtilization(results);
  };

  const onClientChange = (client: IOption | null) => {
    setSelectedClient(client);
    setClientParentSNUtilization(null);
  };

  return (
    <FBoxContainer
      dataTestId="clientInsights"
      titleVariant="h4"
      sx={boxContainerStyle}
      headerStyle={boxContainerHeaderStyle}
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ p: 2, borderRadius: '10px' }}>
            <Box
              sx={{
                display: {
                  xs: 'block',
                  sm: 'flex',
                },
                justifyContent: 'space-between',
                alignItems: 'center',
                mt: 1,
              }}
            >
              <Typography fontSize={21} textAlign="left" pl={1} mb={1} color={FORTA_BLUE.primary}>
                Client information - based on active Treatment plan
              </Typography>
              <FHAutocomplete
                inputHeight={40}
                data-testid="clientSelect"
                sx={{
                  width: {
                    xs: '100%',
                    sm: 300,
                  },
                  boxShadow: 'none',
                  '& .MuiInputBase-input::placeholder': {
                    color: FORTA_GRAY4,
                  },
                }}
                placeholder="Select client"
                disabled={clientLoading}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                options={clientList.map((client: IClient) => ({
                  id: client.id,
                  label: `${client.first_name} ${client.last_name}`,
                }))}
                renderOption={(props: any, option: IOption) => {
                  return (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  );
                }}
                value={selectedClient}
                onChange={(_: any, newValue: IOption | null) => {
                  onClientChange(newValue);
                }}
              />
            </Box>
          </Box>
        </Grid>
        <Grid item sm={12} md={12} lg={showDocumentUtilizationSummary && selectedClient ? 6 : 12}>
          <Box sx={{ backgroundColor: FORTA_WHITE, p: 1, borderRadius: '10px', height: '100%' }}>
            <ClientInsightDetails
              tpInfoLoading={tpInfoLoading}
              treatmentPlanInfo={treatmentPlanInfo}
              selectedClient={selectedClient}
            />
          </Box>
        </Grid>
        {showDocumentUtilizationSummary && selectedClient && (
          <Grid item sm={12} md={12} lg={6}>
            <SixWeekDocumentUtilizationSummary
              selectedClient={selectedClient}
              data={sixWeeksUtilizationData}
              clientParentUtilLoading={clientParentUtilizationLoading}
              sessionUtilLoading={sessionUtilizationLoading}
            />
          </Grid>
        )}
      </Grid>
    </FBoxContainer>
  );
}

export default ClientInsights;
